import { QuoteStatus } from '@pelicargo/types';
import { useCallback, useEffect, useState } from 'react';

import { trpc } from '../../config/trpc';
import { API } from '../../utils/apiTypes';
import { getBestQuoteVariants } from '../../utils/getTotalCost';
import { calculateTotalCostWithoutMarkup } from '../useQuoteVariantHelper';

type QuoteWithTotalCost = API.QuoteWithTotalCost;
type QuoteStatusType = API.QuoteWithTotalCost['status'];

export const cheapestQuoteSort = (quotes: QuoteWithTotalCost[]) => {
  return quotes.toSorted((q1, q2) => {
    if (!q1.quote_variants?.length && !q2.quote_variants?.length) {
      return 0;
    } else if (!q1.quote_variants?.length) {
      return 1;
    } else if (!q2.quote_variants?.length) {
      return -1;
    }
    const bestq1 = getBestQuoteVariants(q1.quote_variants);
    const bestq2 = getBestQuoteVariants(q2.quote_variants);
    const q1rate = calculateTotalCostWithoutMarkup(bestq1[0]);
    const q2rate = calculateTotalCostWithoutMarkup(bestq2[0]);

    return q1rate.totalCostWithoutMarkup - q2rate.totalCostWithoutMarkup;
  });
};

const processAndSortQuotes = (quotes: QuoteWithTotalCost[]) => {
  if (!quotes.length) return [];

  const priorityTypes: QuoteStatusType[] = [QuoteStatus.Confirmed, QuoteStatus.InProgress];
  const sortedQuotes = cheapestQuoteSort(quotes.filter((quote) => priorityTypes.includes(quote.status)));

  if (sortedQuotes.length) sortedQuotes[0].is_cheapest = true;
  quotes.forEach((quote) => {
    if (quote.id !== sortedQuotes[0]?.id) quote.is_cheapest = false;
  });

  const expiredOrPendingQuotes = quotes.filter((quote) => !priorityTypes.includes(quote.status));
  return [...sortedQuotes, ...expiredOrPendingQuotes];
};

export const useQuotes = (requestId: number) => {
  const { data, isLoading, error } = trpc.getRequestQuote.useQuery({ id: requestId }, { refetchOnWindowFocus: false });
  const [quotes, setQuotes] = useState<QuoteWithTotalCost[]>([]);

  useEffect(() => {
    const processedQuotes = processAndSortQuotes(data?.quotes?.length ? data?.quotes : quotes);
    setQuotes(processedQuotes);

    // don't include the quotes because it will loop infinitely...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.quotes]);

  // Update quotes when a new quote is created or updated
  const updateQuotes = useCallback(
    (newQuote: QuoteWithTotalCost, { isNew = false, id }: { isNew?: boolean; id?: number }) => {
      setQuotes((currentQuotes) => {
        const updatedQuotes = isNew
          ? [...currentQuotes, newQuote]
          : id !== undefined
            ? currentQuotes.filter((quote) => quote.id !== id)
            : currentQuotes.map((quote) => (quote.id === newQuote.id ? newQuote : quote));
        return processAndSortQuotes(updatedQuotes);
      });
    },
    [setQuotes],
  );

  trpc.onQuoteCreated.useSubscription(
    { requestId },
    {
      onData: (newData: any) => {
        if (requestId) updateQuotes(newData, { isNew: true });
      },
      onError: (error) => console.log('onQuoteCreated.error: ', error),
    },
  );

  trpc.onQuoteUpdated.useSubscription(
    { requestId },
    {
      onData: (newData: any) => {
        if (requestId) updateQuotes(newData, {});
      },
      onError: (error) => console.log('onQuoteUpdated.error: ', error),
    },
  );

  trpc.onQuoteDeleted.useSubscription({ requestId } as any, {
    onData: (deletedQuoteId: number) => {
      updateQuotes(null, { id: deletedQuoteId });
    },
    onError: (err: any) => {
      console.log('onQuoteUpdated error: ', err);
    },
  });

  return {
    quotes,
    isLoading,
    error,
  };
};
